import React, { useContext, useEffect, useRef, memo } from "react";
import Link from "next/link";

import { GlobalContext } from "@/contexts/GlobalContext";
import { CartContext } from "@/contexts/CartContext";

import useFetch from "@/hooks/useFetchData";

import { calculateDiscount } from "@/utils/discounts.utils";

import { Cart as CartInt } from "@/interfaces/SharedInterfaces";

import styles from "@/styles/cart.module.scss";

const Cart = () => {
    const { globalState: { apiUrl } } = useContext(GlobalContext);
    const { cartState: { 
        cart, 
        cartId, 
        cartTotal, 
        cartCount, 
        discountCode, 
        discountValue 
    }, dispatchCart } = useContext(CartContext);
    const { useFetchData } = useFetch();

    const cartMounted = useRef<boolean>(false);

    useEffect(() => {
        if (cartId && discountCode) {
            fetchDiscountCode();
        }
    }, [cartId, discountCode]);

    useEffect(() => {
        cartMounted.current = false;

        if (cartId && !cart) {
            fetchUserCart();
        }

        return () => {
            cartMounted.current = true;
        }
    }, [cartId, cart]);

    useEffect(() => {
        if (cart) {
            const count = calculateCartItemsCount(cart);
            dispatchCart({ type: "SET_CART_COUNT", cartCount: count });
        }
    }, [cart]);

    useEffect(() => {
        if (cartTotal && cartTotal > 0 && discountValue) {
            const discVals = calculateDiscount(cartTotal, discountValue);
            dispatchCart({ type: "SET_DISC_AMOUNT", discountAmount: discVals.amount });
            dispatchCart({ type: "SET_DISC_TOTAL", discountTotal: discVals.total });
        }

    }, [cartTotal, discountValue]);

    /* ------------------------------------ API FUNCTIONS ------------------------------------ */

    const fetchUserCart = async () => {
        const url: string = `${apiUrl}/cart?ucid=${cartId}`;

        try {
            const result = await useFetchData<CartInt>(url);

            if (!result.success) {
                localStorage.removeItem("_ucid");
                dispatchCart({ type: "SET_CART_ID", cartId: null });
                return false;
            }

            if (!cartMounted.current) {
                const cart = calculateQuantityCost(result.data);
                dispatchCart({ type: "SET_CART", cart: cart });
                dispatchCart({ type: "SET_DISC_CODE", discountCode: result.data.d_code });
            }
        } catch (error) {
            console.error(error);
        }
    };

    const fetchDiscountCode = async () => {
        const url: string = `${apiUrl}/cart/promo?dCode=${discountCode}&ucid=${cartId}`;

        try {
            const result = await useFetchData<{code: string, value: number}>(url);

            if (!result.success) {
                dispatchCart({ type: "SET_DISC_CODE", discountCode: null });
                dispatchCart({ type: "SET_DISC_VALUE", discountValue: null });
                return false;
            }

            dispatchCart({ type: "SET_DISC_CODE", discountCode: result.data.code });
            dispatchCart({ type: "SET_DISC_VALUE", discountValue: result.data.value });
        } catch (error) {
            console.error(error);
        }
    };

    /* ----------------------------------- HANDLERS/FUNCTIONS/ELEMENTS ----------------------------------- */

    const calculateQuantityCost = (list: CartInt): CartInt => {
        list?.cart_items.forEach(ci => {
            ci.total = ci.price * ci.qty;
        });
        return list;
    };

    const calculateCartItemsCount = (cart: CartInt): number => {
        let itemCount: number = 0;

        cart.cart_items.forEach(ci => {
            itemCount += ci.qty;
        });

        return itemCount;
    };

    const counterClass = `counter${!cart || cart.cart_items.length === 0 ? `--empty` : `--full`}`;

    return (
        <div className={styles["cart-component"]}>
            <Link as={"/cart"} href="/cart">
                <React.Fragment>
                    <i className="fa fa-shopping-cart" aria-hidden="true"></i>
                    <div className={styles[counterClass]}>
                        <span>{cartCount}</span>
                    </div>
                </React.Fragment>
            </Link>
        </div>
    );
};

export default memo(Cart);
